// ─────────────────────────────────────────────────────────────────────────────
// InvoiceDetail.js
// Full invoice detail page — view, edit, PDF, email, status management.
//
// ROUTE: /invoices/:id
//
// FEATURES:
//   - View full invoice with line items, totals, company/customer details
//   - Edit invoice (Draft only)
//   - Status: Draft → Sent → Paid / Overdue / Cancelled
//   - PDF generation (same style as quotes)
//   - Email to customer via mailto
//   - Delete invoice
//
// TO ADD STRIPE PAYMENT REQUEST: Add a "Send Payment Link" button here
// once Stripe Connect is configured.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import Sidebar from '../components/Sidebar'
import { useNavigate, useParams } from 'react-router-dom'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import TrialBanner from '../components/TrialBanner'

export default function InvoiceDetail({ session }) {
  const { id } = useParams()
  const navigate = useNavigate()

  // ── State ──────────────────────────────────────────────────────────────────
  const [invoice, setInvoice] = useState(null)
  const [items, setItems] = useState([])
  const [company, setCompany] = useState(null)
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showEditModal, setShowEditModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const [form, setForm] = useState({
    customer_id: '',
    reference: '',
    issue_date: '',
    due_date: '',
    notes: '',
    payment_terms: '',
    terms_conditions: '',
    vat_enabled: true,
    vat_rate: 20,
    items: [],
  })

  // ── Data fetching ──────────────────────────────────────────────────────────
  useEffect(() => { fetchInvoice() }, [])

  const fetchInvoice = async () => {
    const { data: profile } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', session.user.id)
      .single()

    if (profile?.company_id) {
      const [{ data: inv }, { data: i }, { data: co }, { data: cu }] = await Promise.all([
        supabase.from('invoices')
          .select(`*, customers(customer_name, customer_type, email, phone, address)`)
          .eq('id', id)
          .single(),
        supabase.from('invoice_items')
          .select('*')
          .eq('invoice_id', id)
          .order('created_at'),
        supabase.from('companies')
          .select('*')
          .eq('id', profile.company_id)
          .single(),
        supabase.from('customers')
          .select('*')
          .eq('company_id', profile.company_id)
          .order('customer_name'),
      ])
      setInvoice(inv)
      setItems(i || [])
      setCompany(co)
      setCustomers(cu || [])
    }
    setLoading(false)
  }

  // ── Edit modal ─────────────────────────────────────────────────────────────
  const openEdit = () => {
    setForm({
      customer_id: invoice.customer_id || '',
      reference: invoice.reference || '',
      issue_date: invoice.issue_date || '',
      due_date: invoice.due_date || '',
      notes: invoice.notes || '',
      payment_terms: invoice.payment_terms || '',
      terms_conditions: invoice.terms_conditions || '',
      vat_enabled: invoice.vat_enabled ?? true,
      vat_rate: invoice.vat_rate || 20,
      items: items.map(i => ({
        id: i.id,
        description: i.description,
        quantity: i.quantity,
        unit_price: i.unit_price,
        total: i.total,
      })),
    })
    setError(null)
    setShowEditModal(true)
  }

  // ── Line item helpers ──────────────────────────────────────────────────────
  const updateItem = (index, field, value) => {
    const newItems = [...form.items]
    newItems[index] = { ...newItems[index], [field]: value }
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total = parseFloat(newItems[index].quantity || 0) * parseFloat(newItems[index].unit_price || 0)
    }
    setForm({ ...form, items: newItems })
  }

  const addItem = () => setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit_price: 0, total: 0 }] })

  const removeItem = (index) => {
    if (form.items.length === 1) return
    setForm({ ...form, items: form.items.filter((_, i) => i !== index) })
  }

  const subtotal = form.items.reduce((sum, item) => sum + parseFloat(item.total || 0), 0)
  const vatAmount = form.vat_enabled ? subtotal * (parseFloat(form.vat_rate) / 100) : 0
  const grandTotal = subtotal + vatAmount

  // ── Save edits ─────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.customer_id) { setError('Please select a customer.'); return }
    if (form.items.some(i => !i.description.trim())) { setError('All line items need a description.'); return }
    setSaving(true)
    setError(null)

    const { error: updateError } = await supabase.from('invoices').update({
      customer_id: form.customer_id,
      reference: form.reference.trim(),
      issue_date: form.issue_date || null,
      due_date: form.due_date || null,
      notes: form.notes.trim(),
      payment_terms: form.payment_terms.trim(),
      terms_conditions: form.terms_conditions.trim(),
      vat_enabled: form.vat_enabled,
      vat_rate: parseFloat(form.vat_rate),
      subtotal,
      vat_amount: vatAmount,
      total: grandTotal,
    }).eq('id', id)

    if (updateError) { setError(updateError.message); setSaving(false); return }

    await supabase.from('invoice_items').delete().eq('invoice_id', id)
    await supabase.from('invoice_items').insert(
      form.items.map(item => ({
        invoice_id: id,
        description: item.description,
        quantity: parseFloat(item.quantity),
        unit_price: parseFloat(item.unit_price),
        total: parseFloat(item.total),
      }))
    )

    setShowEditModal(false)
    fetchInvoice()
    setSaving(false)
  }

  // ── Status change ──────────────────────────────────────────────────────────
  const handleStatusChange = async (newStatus) => {
    await supabase.from('invoices').update({ status: newStatus }).eq('id', id)
    setInvoice(prev => ({ ...prev, status: newStatus }))
  }

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return
    await supabase.from('invoices').delete().eq('id', id)
    navigate('/invoices')
  }

  // ── Email ──────────────────────────────────────────────────────────────────
  const handleEmail = () => {
    const invNum = `INV-${String(invoice.invoice_number_numeric).padStart(3, '0')}`
    const subject = `Invoice ${invNum} from ${company?.company_name || 'Us'}`
    const body = `Dear ${invoice.customers?.customer_name || 'Customer'},\n\nPlease find attached invoice ${invNum}.\n\nTotal: £${parseFloat(invoice.total || 0).toFixed(2)}\n${invoice.due_date ? `Due Date: ${new Date(invoice.due_date).toLocaleDateString('en-GB')}\n` : ''}\nKind regards,\n${company?.company_name || ''}\n${company?.phone || ''}\n${company?.email || ''}`
    window.location.href = `mailto:${invoice.customers?.email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  // ── PDF generation ─────────────────────────────────────────────────────────
  // Matches the quote PDF style exactly — same layout, colours, fonts
  const handlePDF = () => {
    const doc = new jsPDF()
    const invNum = `INV-${String(invoice.invoice_number_numeric).padStart(3, '0')}`
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()

    const navy   = [30, 58, 95]
    const blue   = [37, 99, 235]
    const grey   = [100, 116, 139]
    const light  = [248, 250, 252]
    const border = [226, 232, 240]
    const black  = [17, 24, 39]

    // ── Header ──
    doc.setFillColor(255, 255, 255)
    doc.rect(0, 0, pageWidth, 32, 'F')
    doc.setDrawColor(...border)
    doc.setLineWidth(0.3)
    doc.line(0, 32, pageWidth, 32)

    // Company logo
    if (company?.logo_url) {
      try { doc.addImage(company.logo_url, 'PNG', 14, 4, 24, 24) } catch (e) {}
    }

    // ── Company details ──
    let y = 44
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(11)
    doc.setTextColor(...black)
    doc.text(company?.company_name || '', 14, y)

    y += 8
    const companyDetails = [
      company?.address    ? { label: 'Address:', value: company.address    } : null,
      company?.email      ? { label: 'Email:',   value: company.email      } : null,
      company?.phone      ? { label: 'Tel:',     value: company.phone      } : null,
      company?.vat_number ? { label: 'VAT No:',  value: company.vat_number } : null,
    ].filter(Boolean)

    companyDetails.forEach(item => {
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(9)
      doc.setTextColor(...grey)
      doc.text(item.label, 14, y)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(...black)
      doc.text(item.value, 38, y)
      y += 5.5
    })

    // ── Customer details (right) ──
    let cy = 44
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(8)
    doc.setTextColor(...grey)
    doc.text('INVOICE TO', pageWidth - 14, cy, { align: 'right' })
    cy += 6

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    doc.setTextColor(...black)
    doc.text(invoice.customers?.customer_name || '', pageWidth - 14, cy, { align: 'right' })
    cy += 5.5

    const customerDetails = [
      invoice.customers?.email   ? { label: 'Email:', value: invoice.customers.email   } : null,
      invoice.customers?.phone   ? { label: 'Tel:',   value: invoice.customers.phone   } : null,
      invoice.customers?.address ? { label: 'Addr:',  value: invoice.customers.address } : null,
    ].filter(Boolean)

    customerDetails.forEach(item => {
      const labelW = doc.getTextWidth(item.label) + 2
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(9)
      doc.setTextColor(...grey)
      doc.text(item.label, pageWidth - 14 - doc.getTextWidth(item.value) - labelW, cy)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(...black)
      doc.text(item.value, pageWidth - 14, cy, { align: 'right' })
      cy += 5.5
    })

    // ── Invoice details bar ──
    y = Math.max(y, cy) + 8
    doc.setFillColor(...light)
    doc.roundedRect(14, y, pageWidth - 28, 20, 2, 2, 'F')
    doc.setDrawColor(...border)
    doc.setLineWidth(0.3)
    doc.roundedRect(14, y, pageWidth - 28, 20, 2, 2, 'S')

    const details = [
      { label: 'Invoice No.', value: invNum },
      { label: 'Issue Date', value: invoice.issue_date ? new Date(invoice.issue_date).toLocaleDateString('en-GB') : '—' },
      { label: 'Due Date', value: invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('en-GB') : '—' },
      { label: 'Reference', value: invoice.reference || '—' },
      { label: 'Status', value: invoice.status },
    ]

    const colW = (pageWidth - 28) / details.length
    details.forEach((d, i) => {
      const x = 14 + i * colW + colW / 2
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(7)
      doc.setTextColor(...grey)
      doc.text(d.label.toUpperCase(), x, y + 7, { align: 'center' })
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(9)
      doc.setTextColor(...black)
      doc.text(d.value, x, y + 14, { align: 'center' })
    })

    y += 28

    // ── Line items table ──
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    doc.setTextColor(...black)
    doc.text('LINE ITEMS', 14, y)
    y += 4

    autoTable(doc, {
      startY: y,
      head: [['Description', 'Qty', 'Unit Price', 'Total']],
      body: items.map(item => [
        item.description,
        item.quantity.toString(),
        `£${parseFloat(item.unit_price).toFixed(2)}`,
        `£${parseFloat(item.total).toFixed(2)}`,
      ]),
      styles: { fontSize: 9, cellPadding: 5, textColor: black },
      headStyles: { fillColor: navy, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
      alternateRowStyles: { fillColor: light },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 20, halign: 'center' },
        2: { cellWidth: 30, halign: 'right' },
        3: { cellWidth: 30, halign: 'right', fontStyle: 'bold' },
      },
      margin: { left: 14, right: 14 },
    })

    y = doc.lastAutoTable.finalY + 8

    // ── Totals ──
    const totalsX = pageWidth - 80
    const totalsW = 66
    const totalsH = invoice.vat_enabled ? 34 : 22

    doc.setFillColor(...light)
    doc.roundedRect(totalsX, y, totalsW, totalsH, 2, 2, 'F')
    doc.setDrawColor(...border)
    doc.setLineWidth(0.3)
    doc.roundedRect(totalsX, y, totalsW, totalsH, 2, 2, 'S')

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.setTextColor(...grey)
    doc.text('Subtotal', totalsX + 4, y + 8)
    doc.setTextColor(...black)
    doc.text(`£${parseFloat(invoice.subtotal || 0).toFixed(2)}`, totalsX + totalsW - 4, y + 8, { align: 'right' })

    if (invoice.vat_enabled) {
      doc.setTextColor(...grey)
      doc.text(`VAT (${invoice.vat_rate}%)`, totalsX + 4, y + 16)
      doc.setTextColor(...black)
      doc.text(`£${parseFloat(invoice.vat_amount || 0).toFixed(2)}`, totalsX + totalsW - 4, y + 16, { align: 'right' })
    }

    const totalY = y + (invoice.vat_enabled ? 26 : 14)
    doc.setDrawColor(...blue)
    doc.setLineWidth(0.5)
    doc.line(totalsX + 4, totalY - 3, totalsX + totalsW - 4, totalY - 3)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(11)
    doc.setTextColor(...blue)
    doc.text('TOTAL', totalsX + 4, totalY + 4)
    doc.text(`£${parseFloat(invoice.total || 0).toFixed(2)}`, totalsX + totalsW - 4, totalY + 4, { align: 'right' })

    y = totalY + 14

    // ── Notes & Terms ──
    if (invoice.notes || invoice.payment_terms || invoice.terms_conditions) {
      doc.setDrawColor(...border)
      doc.setLineWidth(0.3)
      doc.line(14, y, pageWidth - 14, y)
      y += 6

      if (invoice.notes) {
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(8)
        doc.setTextColor(...grey)
        doc.text('NOTES', 14, y)
        y += 4
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(...black)
        const noteLines = doc.splitTextToSize(invoice.notes, pageWidth - 28)
        doc.text(noteLines, 14, y)
        y += noteLines.length * 4 + 4
      }

      if (invoice.payment_terms) {
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(8)
        doc.setTextColor(...grey)
        doc.text('PAYMENT TERMS', 14, y)
        y += 4
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(...black)
        doc.text(invoice.payment_terms, 14, y)
        y += 8
      }

      if (invoice.terms_conditions) {
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(8)
        doc.setTextColor(...grey)
        doc.text('TERMS & CONDITIONS', 14, y)
        y += 4
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(...black)
        const tLines = doc.splitTextToSize(invoice.terms_conditions, pageWidth - 28)
        doc.text(tLines, 14, y)
      }
    }

    // ── Footer ──
    doc.setFillColor(...light)
    doc.rect(0, pageHeight - 14, pageWidth, 14, 'F')
    doc.setDrawColor(...border)
    doc.setLineWidth(0.3)
    doc.line(0, pageHeight - 14, pageWidth, pageHeight - 14)
    doc.setTextColor(...grey)
    doc.setFontSize(7)
    doc.setFont('helvetica', 'normal')
    doc.text(company?.company_name || '', 14, pageHeight - 5)
    if (company?.email) doc.text(company.email, pageWidth / 2, pageHeight - 5, { align: 'center' })
    doc.text(`Generated ${new Date().toLocaleDateString('en-GB')}`, pageWidth - 14, pageHeight - 5, { align: 'right' })

    doc.save(`${invNum}-${invoice.customers?.customer_name || 'invoice'}.pdf`)
  }

  // ── Status style ───────────────────────────────────────────────────────────
  const statusStyle = (status) => {
    const map = {
      Draft:     { background: '#F1F5F9', color: '#64748B' },
      Sent:      { background: '#EFF6FF', color: '#2563EB' },
      Paid:      { background: '#F0FDF4', color: '#15803D' },
      Overdue:   { background: '#FFF1F2', color: '#BE123C' },
      Cancelled: { background: '#F1F5F9', color: '#94A3B8' },
    }
    return {
      ...(map[status] || map.Draft),
      padding: '4px 12px', borderRadius: '999px',
      fontSize: '13px', fontWeight: '600',
      display: 'inline-block', whiteSpace: 'nowrap',
    }
  }

  // ── Shared styles ──────────────────────────────────────────────────────────
  const inputStyle = {
    width: '100%', height: '44px', borderRadius: '10px',
    border: '1.5px solid #E2E8F0', padding: '0 14px',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box',
    background: '#FAFAFA', fontFamily: 'Inter, sans-serif',
  }

  const labelStyle = {
    display: 'block', fontSize: '13px', fontWeight: '600',
    color: '#374151', marginBottom: '6px',
  }

  // ── Loading / not found ────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: 'Inter, sans-serif', background: '#F7F8FC' }}>
      <Sidebar session={session} />
      <TrialBanner session={session} />
      <div style={{ marginLeft: '240px', flex: 1, padding: '36px 40px', color: '#94A3B8' }}>Loading...</div>
    </div>
  )

  if (!invoice) return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: 'Inter, sans-serif', background: '#F7F8FC' }}>
      <Sidebar session={session} />
      <div style={{ marginLeft: '240px', flex: 1, padding: '36px 40px' }}>
        <p style={{ color: '#94A3B8' }}>Invoice not found.</p>
        <button onClick={() => navigate('/invoices')} style={{
          padding: '8px 20px', borderRadius: '8px',
          background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
          color: 'white', fontSize: '13px', fontWeight: '600',
          border: 'none', cursor: 'pointer',
        }}>Back to Invoices</button>
      </div>
    </div>
  )

  const invNum = `INV-${String(invoice.invoice_number_numeric).padStart(3, '0')}`
  const isOverdue = invoice.due_date && new Date(invoice.due_date) < new Date() && invoice.status !== 'Paid' && invoice.status !== 'Cancelled'

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{
      display: 'flex', minHeight: '100vh',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      background: '#F7F8FC',
    }}>
      <Sidebar session={session} />

      <div style={{ marginLeft: '240px', flex: 1, padding: '36px 40px' }}>

        {/* ── Back button ── */}
        <button onClick={() => navigate('/invoices')} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: '13px', color: '#64748B', fontWeight: '600',
          padding: 0, marginBottom: '20px',
          display: 'flex', alignItems: 'center', gap: '6px',
          fontFamily: 'Inter, sans-serif',
        }}>← Back to Invoices</button>
        <TrialBanner session={session} />

        {/* ── Page header ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
              <h1 style={{ fontSize: '22px', fontWeight: '800', color: '#0F172A', margin: 0, letterSpacing: '-0.4px' }}>
                Invoice {invNum}
              </h1>
              <span style={statusStyle(invoice.status)}>{invoice.status}</span>
              {isOverdue && invoice.status === 'Sent' && (
                <span style={{ ...statusStyle('Overdue'), fontSize: '11px' }}>Overdue</span>
              )}
            </div>
            <p style={{ fontSize: '13px', color: '#94A3B8', margin: 0 }}>
              Issued {invoice.issue_date ? new Date(invoice.issue_date).toLocaleDateString('en-GB') : new Date(invoice.created_at).toLocaleDateString('en-GB')}
              {invoice.quote_id && (
                <span style={{ marginLeft: '8px', color: '#2563EB', fontWeight: '600' }}>
                  · Converted from quote
                </span>
              )}
            </p>
          </div>

          {/* ── Action buttons ── */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {invoice.status === 'Draft' && (
              <button onClick={openEdit} style={{
                padding: '8px 16px', borderRadius: '8px', fontSize: '13px',
                fontWeight: '600', border: '1.5px solid #E2E8F0',
                background: 'white', color: '#374151', cursor: 'pointer',
              }}>Edit Invoice</button>
            )}
            {invoice.status === 'Draft' && (
              <button onClick={() => handleStatusChange('Sent')} style={{
                padding: '8px 16px', borderRadius: '8px', fontSize: '13px',
                fontWeight: '600', border: 'none',
                background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                color: 'white', cursor: 'pointer',
              }}>Mark as Sent</button>
            )}
            {(invoice.status === 'Sent' || invoice.status === 'Overdue') && (
              <button onClick={() => handleStatusChange('Paid')} style={{
                padding: '8px 16px', borderRadius: '8px', fontSize: '13px',
                fontWeight: '600', border: 'none',
                background: '#15803D', color: 'white', cursor: 'pointer',
              }}>Mark as Paid</button>
            )}
            {invoice.status === 'Sent' && (
              <button onClick={() => handleStatusChange('Overdue')} style={{
                padding: '8px 16px', borderRadius: '8px', fontSize: '13px',
                fontWeight: '600', border: '1.5px solid #FECDD3',
                background: 'white', color: '#BE123C', cursor: 'pointer',
              }}>Mark as Overdue</button>
            )}
            {(invoice.status === 'Draft' || invoice.status === 'Sent') && (
              <button onClick={() => handleStatusChange('Cancelled')} style={{
                padding: '8px 16px', borderRadius: '8px', fontSize: '13px',
                fontWeight: '600', border: '1.5px solid #E2E8F0',
                background: 'white', color: '#94A3B8', cursor: 'pointer',
              }}>Cancel</button>
            )}
            <button onClick={handlePDF} style={{
              padding: '8px 16px', borderRadius: '8px', fontSize: '13px',
              fontWeight: '600', border: '1.5px solid #E2E8F0',
              background: 'white', color: '#374151', cursor: 'pointer',
            }}>Download PDF</button>
            <button onClick={handleEmail} style={{
              padding: '8px 16px', borderRadius: '8px', fontSize: '13px',
              fontWeight: '600', border: '1.5px solid #E2E8F0',
              background: 'white', color: '#374151', cursor: 'pointer',
            }}>Send by Email</button>
            <button onClick={handleDelete} style={{
              padding: '8px 16px', borderRadius: '8px', fontSize: '13px',
              fontWeight: '600', border: '1.5px solid #FECDD3',
              background: 'white', color: '#BE123C', cursor: 'pointer',
            }}>Delete</button>
          </div>
        </div>

        {/* ── Two column layout ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '24px' }}>

          {/* ── Left column ── */}
          <div>

            {/* Company + customer header card */}
            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #EAECF0', padding: '24px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  {company?.logo_url && (
                    <img src={company.logo_url} alt="logo"
                      style={{ height: '48px', objectFit: 'contain', marginBottom: '12px', display: 'block' }} />
                  )}
                  <p style={{ fontSize: '15px', fontWeight: '800', color: '#0F172A', margin: '0 0 4px' }}>
                    {company?.company_name}
                  </p>
                  {company?.address && <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 2px' }}>{company.address}</p>}
                  {company?.email && <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 2px' }}>{company.email}</p>}
                  {company?.phone && <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 2px' }}>{company.phone}</p>}
                  {company?.vat_number && <p style={{ fontSize: '13px', color: '#64748B', margin: 0 }}>VAT: {company.vat_number}</p>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: '11px', fontWeight: '600', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 4px' }}>Invoice To</p>
                  <p style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A', margin: '0 0 4px' }}>{invoice.customers?.customer_name}</p>
                  {invoice.customers?.email && <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 2px' }}>{invoice.customers.email}</p>}
                  {invoice.customers?.phone && <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 2px' }}>{invoice.customers.phone}</p>}
                  {invoice.customers?.address && <p style={{ fontSize: '13px', color: '#64748B', margin: 0 }}>{invoice.customers.address}</p>}
                </div>
              </div>
            </div>

            {/* Line items */}
            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #EAECF0', overflow: 'hidden', marginBottom: '20px' }}>
              <div style={{ padding: '16px 24px', borderBottom: '1px solid #EAECF0' }}>
                <h2 style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A', margin: 0 }}>Line Items</h2>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 100px', padding: '10px 24px', background: '#F8FAFC', borderBottom: '1px solid #EAECF0' }}>
                {['Description', 'Qty', 'Unit Price', 'Total'].map((h, i) => (
                  <span key={i} style={{ fontSize: '11px', fontWeight: '600', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</span>
                ))}
              </div>
              {items.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#94A3B8', fontSize: '13px' }}>No line items</div>
              ) : (
                items.map((item, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 100px', padding: '12px 24px', alignItems: 'center', borderBottom: i < items.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                    <span style={{ fontSize: '13px', color: '#0F172A', fontWeight: '500' }}>{item.description}</span>
                    <span style={{ fontSize: '13px', color: '#64748B' }}>{item.quantity}</span>
                    <span style={{ fontSize: '13px', color: '#64748B' }}>£{parseFloat(item.unit_price).toFixed(2)}</span>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: '#0F172A' }}>£{parseFloat(item.total).toFixed(2)}</span>
                  </div>
                ))
              )}

              {/* Totals */}
              <div style={{ padding: '16px 24px', borderTop: '1px solid #EAECF0', background: '#F8FAFC' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <div style={{ width: '240px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontSize: '13px', color: '#64748B' }}>Subtotal</span>
                      <span style={{ fontSize: '13px', color: '#0F172A' }}>£{parseFloat(invoice.subtotal || 0).toFixed(2)}</span>
                    </div>
                    {invoice.vat_enabled && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ fontSize: '13px', color: '#64748B' }}>VAT ({invoice.vat_rate}%)</span>
                        <span style={{ fontSize: '13px', color: '#0F172A' }}>£{parseFloat(invoice.vat_amount || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div style={{ height: '1px', background: '#E2E8F0', margin: '8px 0' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '15px', fontWeight: '700', color: '#0F172A' }}>Total</span>
                      <span style={{ fontSize: '18px', fontWeight: '800', color: '#2563EB' }}>£{parseFloat(invoice.total || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes & Terms */}
            {(invoice.notes || invoice.payment_terms || invoice.terms_conditions) && (
              <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #EAECF0', padding: '24px' }}>
                {invoice.notes && (
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ fontSize: '11px', fontWeight: '600', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 6px' }}>Notes</p>
                    <p style={{ fontSize: '13px', color: '#64748B', margin: 0 }}>{invoice.notes}</p>
                  </div>
                )}
                {invoice.payment_terms && (
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ fontSize: '11px', fontWeight: '600', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 6px' }}>Payment Terms</p>
                    <p style={{ fontSize: '13px', color: '#64748B', margin: 0 }}>{invoice.payment_terms}</p>
                  </div>
                )}
                {invoice.terms_conditions && (
                  <div>
                    <p style={{ fontSize: '11px', fontWeight: '600', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 6px' }}>Terms & Conditions</p>
                    <p style={{ fontSize: '13px', color: '#64748B', margin: 0 }}>{invoice.terms_conditions}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Right column ── */}
          <div>

            {/* Invoice details card */}
            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #EAECF0', padding: '24px', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A', margin: '0 0 16px', paddingBottom: '12px', borderBottom: '1px solid #F1F5F9' }}>Invoice Details</h2>
              {[
                { label: 'Invoice Number', value: invNum },
                { label: 'Status', value: invoice.status, isStatus: true },
                { label: 'Issue Date', value: invoice.issue_date ? new Date(invoice.issue_date).toLocaleDateString('en-GB') : '—' },
                {
                  label: 'Due Date',
                  value: invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('en-GB') : '—',
                  isOverdue: isOverdue,
                },
                { label: 'Reference', value: invoice.reference || '—' },
                { label: 'Customer Type', value: invoice.customers?.customer_type || '—' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < 5 ? '1px solid #F8FAFC' : 'none' }}>
                  <span style={{ fontSize: '12px', color: '#94A3B8', fontWeight: '500' }}>{item.label}</span>
                  {item.isStatus
                    ? <span style={statusStyle(item.value)}>{item.value}</span>
                    : <span style={{ fontSize: '13px', fontWeight: '600', color: item.isOverdue ? '#BE123C' : '#0F172A' }}>{item.value}</span>
                  }
                </div>
              ))}
            </div>

            {/* Customer details card */}
            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #EAECF0', padding: '24px' }}>
              <h2 style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A', margin: '0 0 16px', paddingBottom: '12px', borderBottom: '1px solid #F1F5F9' }}>Customer Details</h2>
              {[
                { label: 'Name', value: invoice.customers?.customer_name || '—' },
                { label: 'Email', value: invoice.customers?.email || '—' },
                { label: 'Phone', value: invoice.customers?.phone || '—' },
                { label: 'Address', value: invoice.customers?.address || '—' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: i < 3 ? '1px solid #F8FAFC' : 'none' }}>
                  <span style={{ fontSize: '12px', color: '#94A3B8', fontWeight: '500', flexShrink: 0 }}>{item.label}</span>
                  <span style={{ fontSize: '13px', fontWeight: '500', color: '#0F172A', textAlign: 'right', maxWidth: '180px' }}>{item.value}</span>
                </div>
              ))}
              <button onClick={() => navigate('/customers')} style={{
                width: '100%', marginTop: '16px', padding: '8px',
                borderRadius: '8px', border: '1.5px solid #E2E8F0',
                background: 'white', fontSize: '12px', fontWeight: '600',
                color: '#64748B', cursor: 'pointer',
              }}>View Customer</button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Edit Invoice Modal ───────────────────────────────────────────────────
          Only available when invoice is in Draft status.
      ─────────────────────────────────────────────────────────────────────── */}
      {showEditModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 100, padding: '24px',
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '32px',
            width: '100%', maxWidth: '700px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            maxHeight: '90vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 }}>
                Edit Invoice {invNum}
              </h2>
              <div onClick={() => setShowEditModal(false)} style={{
                width: '32px', height: '32px', borderRadius: '8px', background: '#F1F5F9',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', fontSize: '16px', color: '#64748B',
              }}>×</div>
            </div>

            {error && (
              <div style={{
                background: '#FFF1F2', border: '1px solid #FECDD3',
                borderRadius: '8px', padding: '12px',
                color: '#BE123C', fontSize: '13px', marginBottom: '16px',
              }}>{error}</div>
            )}

            {/* Customer + Reference */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={labelStyle}>Customer *</label>
                <select value={form.customer_id}
                  onChange={e => setForm({ ...form, customer_id: e.target.value })}
                  style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value="">Select customer...</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.customer_name} ({c.customer_type || 'Business'})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Reference / PO Number</label>
                <input type="text" value={form.reference}
                  onChange={e => setForm({ ...form, reference: e.target.value })}
                  placeholder="e.g. PO-12345" style={inputStyle} />
              </div>
            </div>

            {/* Dates */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={labelStyle}>Issue Date</label>
                <input type="date" value={form.issue_date}
                  onChange={e => setForm({ ...form, issue_date: e.target.value })}
                  style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Due Date</label>
                <input type="date" value={form.due_date}
                  onChange={e => setForm({ ...form, due_date: e.target.value })}
                  style={inputStyle} />
              </div>
            </div>

            {/* Line items */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <label style={{ ...labelStyle, margin: 0 }}>Line Items</label>
                <button onClick={addItem} style={{
                  padding: '4px 12px', borderRadius: '6px', fontSize: '12px',
                  fontWeight: '600', border: '1.5px solid #2563EB',
                  background: 'white', color: '#2563EB', cursor: 'pointer',
                }}>+ Add Item</button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 100px 32px', gap: '8px', marginBottom: '6px', padding: '0 4px' }}>
                {['Description', 'Qty', 'Unit Price', 'Total', ''].map((h, i) => (
                  <span key={i} style={{ fontSize: '11px', fontWeight: '600', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</span>
                ))}
              </div>

              {form.items.map((item, index) => (
                <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 100px 32px', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                  <input type="text" value={item.description}
                    onChange={e => updateItem(index, 'description', e.target.value)}
                    placeholder="e.g. Labour" style={{ ...inputStyle, height: '40px' }} />
                  <input type="number" value={item.quantity}
                    onChange={e => updateItem(index, 'quantity', e.target.value)}
                    min="0" style={{ ...inputStyle, height: '40px' }} />
                  <input type="number" value={item.unit_price}
                    onChange={e => updateItem(index, 'unit_price', e.target.value)}
                    min="0" placeholder="0.00" style={{ ...inputStyle, height: '40px' }} />
                  <div style={{
                    height: '40px', borderRadius: '10px', background: '#F8FAFC',
                    border: '1.5px solid #E2E8F0', display: 'flex', alignItems: 'center',
                    padding: '0 14px', fontSize: '14px', fontWeight: '600', color: '#0F172A',
                  }}>£{parseFloat(item.total || 0).toFixed(2)}</div>
                  <button onClick={() => removeItem(index)} disabled={form.items.length === 1} style={{
                    width: '32px', height: '32px', borderRadius: '6px',
                    border: '1.5px solid #FECDD3', background: 'white',
                    color: '#BE123C', cursor: form.items.length === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    opacity: form.items.length === 1 ? 0.3 : 1,
                  }}>×</button>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div style={{ background: '#F8FAFC', borderRadius: '10px', border: '1px solid #E2E8F0', padding: '16px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{ width: '280px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '13px', color: '#64748B' }}>Subtotal</span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A' }}>£{subtotal.toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div onClick={() => setForm({ ...form, vat_enabled: !form.vat_enabled })} style={{
                        width: '18px', height: '18px', borderRadius: '4px',
                        border: `2px solid ${form.vat_enabled ? '#2563EB' : '#E2E8F0'}`,
                        background: form.vat_enabled ? '#2563EB' : 'white',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                      }}>
                        {form.vat_enabled && (
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                        )}
                      </div>
                      <span style={{ fontSize: '13px', color: '#64748B' }}>VAT</span>
                      {form.vat_enabled && (
                        <>
                          <input type="number" value={form.vat_rate}
                            onChange={e => setForm({ ...form, vat_rate: e.target.value })}
                            style={{ width: '50px', height: '28px', borderRadius: '6px', border: '1.5px solid #E2E8F0', padding: '0 8px', fontSize: '13px', outline: 'none', background: 'white' }} />
                          <span style={{ fontSize: '13px', color: '#64748B' }}>%</span>
                        </>
                      )}
                    </div>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A' }}>£{vatAmount.toFixed(2)}</span>
                  </div>
                  <div style={{ height: '1px', background: '#E2E8F0', margin: '8px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A' }}>Total</span>
                    <span style={{ fontSize: '16px', fontWeight: '800', color: '#2563EB' }}>£{grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes + Payment Terms + T&Cs */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Notes</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                placeholder="Any additional notes..."
                style={{ width: '100%', height: '80px', borderRadius: '10px', border: '1.5px solid #E2E8F0', padding: '12px 14px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: '#FAFAFA', resize: 'vertical', fontFamily: 'Inter, sans-serif' }} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Payment Terms</label>
              <input type="text" value={form.payment_terms}
                onChange={e => setForm({ ...form, payment_terms: e.target.value })}
                placeholder="e.g. Payment due within 30 days" style={inputStyle} />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>Terms & Conditions</label>
              <textarea value={form.terms_conditions} onChange={e => setForm({ ...form, terms_conditions: e.target.value })}
                placeholder="Your terms and conditions..."
                style={{ width: '100%', height: '80px', borderRadius: '10px', border: '1.5px solid #E2E8F0', padding: '12px 14px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: '#FAFAFA', resize: 'vertical', fontFamily: 'Inter, sans-serif' }} />
            </div>

            {/* Modal buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowEditModal(false)} style={{
                flex: 1, height: '44px', borderRadius: '10px',
                background: 'white', border: '1.5px solid #E2E8F0',
                fontSize: '14px', fontWeight: '600', color: '#64748B', cursor: 'pointer',
              }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{
                flex: 1, height: '44px', borderRadius: '10px',
                background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                color: 'white', fontSize: '14px', fontWeight: '700',
                border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
              }}>{saving ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}